// Third-party
import React from 'react';
import { Waypoint } from 'react-waypoint';
import { SortingRule } from 'react-table';
import classNames from 'classnames';

// Components
import { LoadingSpinner } from '../../components/common';
import { PortfoliosTable } from '../../components/tables';
import { SearchInput } from '../../components/inputs';

// Utils
import { formatMessage } from '../../../utils/i18n';
import { getPortfolioMissingHoldings } from '../../../utils/portfolio/utils';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../utils/permissions';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../enums/Portfolio';
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { Typography } from '@capintel/component-lib/typography';

type Props = {
  user: User;
  query?: string;
  searching: boolean;
  initialized: boolean;
  models: Portfolio[];
  enterprise: EnterpriseDetails;
  comparingModelIds: string[];
  hasMore: boolean;
  onSearch: (query: string) => void;
  onSort: (sorting: SortingRule[]) => void;
  onLoadMore: () => void;
  onDeletePortfolio: (portfolios: Portfolio[]) => void;
  onEditPortfolio: (portfolio: Portfolio) => void;
  onCompare: (portfolio: Portfolio) => void;
  onCompareSelected: (portfolios: Portfolio[]) => void;
};

class LegacyEnterpriseModelsTab extends React.Component<Props> {
  containerRef = React.createRef<HTMLDivElement>();

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  renderPortfoliosSection = (title: string, portfolios: Portfolio[], enterprise?: EnterpriseDetails) => {
    const { searching, hasMore, onLoadMore, initialized } = this.props;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.MER, PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE];

    return (
      <div className="portfolios-section">
        <div className="section-headers">
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
            {title}
          </Typography>
          <div className="section-actions">
            <SearchInput loading={searching} key="models" placeholder={formatMessage('Models.SearchEnterpriseModels')} onChange={this.props.onSearch} />
          </div>
        </div>

        <div className="portfolios">
          {(searching || !initialized) && <LoadingSpinner />}

          <PortfoliosTable<Portfolio>
            portfolios={portfolios}
            className={classNames({ 'visually-hidden': searching || !initialized })}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            onDelete={this.props.onDeletePortfolio}
            onSort={this.props.onSort}
            onEdit={this.props.onEditPortfolio}
            onCompare={this.props.onCompareSelected}
            comparingPortfolioIds={this.props.comparingModelIds}
            selectedTitle="PortfoliosTable.SelectedTitle.Models"
            locked={!!enterprise}
            messageIds={{
              deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedModels',
              deleteSingle: 'PortfoliosTable.Buttons.DeleteModel',
              editSingle: 'PortfoliosTable.Buttons.EditModel',
            }}
            missingProductPredicate={portfolio => getPortfolioMissingHoldings(portfolio).length > 0}
          />

          {hasMore && initialized && !searching && (
            <>
              <Waypoint onEnter={onLoadMore} />
              <LoadingSpinner />
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { enterprise, models } = this.props;
    const title = enterprise && formatMessage('Models.EnterpriseTitle', { name: enterprise.name, count: enterprise.name.endsWith('s') ? 1 : 0 });

    return (
      <div ref={this.containerRef} className="all-models">
        {enterprise && models && title && this.renderPortfoliosSection(title, models, enterprise)}
      </div>
    );
  }
}

export default LegacyEnterpriseModelsTab;
